import React, { createContext, useContext, ReactNode, useState } from 'react'
import { useAuth } from '../auth-context/AuthContext'
import { useFetchWithAuth } from '../../hooks/useFetchWithAuth'
import { VariantType } from '../../lib/CommonLib'
import { apiResponse, ResponseType, showSnackbar } from '../../utils/Utils'

interface ContactContextType {
  categories: any
  fetchCategories: () => void
  sendEmail: (emailData: {
    name: string
    email: string
    subject: string
    category: string
    enquiry: string
  }) => Promise<void>
}

const ContactContext = createContext<ContactContextType | undefined>(undefined)

export const ContactProvider = ({ children }: { children: ReactNode }) => {
  const { setSnackbarOpen, setSnackbarMessage, setVariant, setLoading } = useAuth()
  const fetchWithAuth = useFetchWithAuth()
  const [categories, setCategories] = useState<string[]>([])

  const fetchCategories = async () => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/ContactUs/categories', {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = await apiResponse(response)
      setCategories(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  const sendEmail = async (emailData: {
    name: string
    email: string
    subject: string
    category: string
    enquiry: string
  }) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/ContactUs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      })
      const responseText = await apiResponse(response, ResponseType.text)
      showSnackbar(
        responseText || 'Email sent successfully',
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <ContactContext.Provider value={{ categories, fetchCategories, sendEmail }}>
      {children}
    </ContactContext.Provider>
  )
}

export const useContact = () => {
  const context = useContext(ContactContext)
  if (context === undefined) {
    throw new Error('useContact must be used within a ContactProvider')
  }
  return context
}
